import React, { useState } from 'react';
import { usePageMeta, injectJsonLd } from '@/lib/seo';
import { assets } from '@/lib/assets';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Mail,
  MessageSquare,
  AlertTriangle,
  Clock,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';

export function ContactPage() {
  // Form removed — using direct contact methods instead
  usePageMeta({
    title: 'Contact – n8n2py',
    description: 'Contact the n8n2py team for support, questions about pricing, or general inquiries.',
    image: assets.socialImage,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  });
  React.useEffect(() => {
    const cleanup = injectJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://n8n2py.me/' },
        { '@type': 'ListItem', position: 2, name: 'Contact', item: 'https://n8n2py.me/contact' }
      ]
    });
    return cleanup;
  }, []);

  const contactMethods = [
    {
      icon: <Mail className="h-5 w-5" />,
      title: 'Email Support',
      description: 'Get help with technical issues',
      contact: 'contact@n8n2py.me',
      responseTime: 'Usually within 24 hours'
    },
    {
      icon: <HelpCircle className="h-5 w-5" />,
      title: 'General Inquiries',
      description: 'Questions about features or pricing',
      contact: 'contact@n8n2py.me',
      responseTime: 'Usually within 48 hours'
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="text-center space-y-4 mb-8">
        <div className="flex justify-center">
          <div className="p-3 bg-primary/10 rounded-full">
            <MessageSquare className="h-8 w-8 text-primary" />
          </div>
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Contact Us</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Have a question or need help? We're here to assist you with n8n2py.
        </p>
        <div className="pt-2">
          <Button asChild>
            <a href="mailto:contact@n8n2py.me?subject=Support%20Request%20-%20n8n2py">
              <span className="inline-flex items-center"><Mail className="h-4 w-4 mr-2" /> Email us directly</span>
            </a>
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-1 gap-8">
        {/* Contact Information Only */}
        <div className="space-y-6">
          {/* Contact Methods */}
          <Card>
            <CardHeader>
              <CardTitle>Other Ways to Reach Us</CardTitle>
              <CardDescription>
                Choose the best way to get in touch based on your needs.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {contactMethods.map((method, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 border rounded-lg">
                  <div className="flex-shrink-0 p-2 bg-muted rounded-lg">
                    {method.icon}
                  </div>
                  <div className="space-y-1">
                    <h4 className="font-medium">{method.title}</h4>
                    <p className="text-sm text-muted-foreground">{method.description}</p>
                    <div className="space-y-1">
                      <p className="text-sm font-mono text-foreground">{method.contact}</p>
                      <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{method.responseTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* FAQ Notice */}
          <Alert>
            <HelpCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Quick answers:</strong> Check our documentation or tutorial page for common questions about workflow conversion and deployment.
            </AlertDescription>
          </Alert>

          {/* Response Time Notice */}
          <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20">
            <Clock className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              <strong>Response Times:</strong> We typically respond to support requests within 24 hours during business days (Monday-Friday, 9 AM - 5 PM PST).
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
}
